import { useCallback, useState } from "react";
import { getPricesAC, getPricesDC } from "../services/pricemanagment";

export default function useStationPrice() {
  const [stationPrices, setStationPrice] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [originalResponse, setOriginalResponse] = useState(null);

  // useCallback so consumers can depend on this in an effect without the
  // hook's fresh identity re-running it every render. It closes over nothing
  // but the state setters, which React keeps stable.
  const getStationPriceByType = useCallback(async (powerType) => {
    console.log("Fetching station price for type:", powerType);

    if (!powerType) {
      setStationPrice(null);
      setLastUpdated(null);
      setOriginalResponse(null);
      return;
    }

    setIsLoading(true);

    try {
      const res =
        powerType === "AC" ? await getPricesAC() : await getPricesDC();

      if (!res) {
        console.warn("⚠️ API response is empty.");
        setStationPrice(null);
        setLastUpdated(null);
        setOriginalResponse(null);
        setIsLoading(false);
        return;
      }

      console.log("✅ API Response:", res);
      setOriginalResponse(res);
      setLastUpdated(res.last_updated || null);

      const vatRate = res?.vat ?? 19;
      const vatMultiplier = 1 + vatRate / 100;

      const transactionFeeObject =
        res?.elements?.find((el) => el?.price_components[0]?.type === "FLAT")
          ?.price_components[0] || null;

      const transactionFee = transactionFeeObject
        ? Number(transactionFeeObject?.price) * vatMultiplier
        : null;

      const energyPriceObj =
        res?.elements?.find((el) => el?.price_components[0]?.type === "ENERGY")
          ?.price_components[0] || null;

      const energyPrice = energyPriceObj
        ? ((Number(energyPriceObj?.price) * Number(energyPriceObj?.step_size)) /
            1000) *
          vatMultiplier
        : null;

      const timePriceObj =
        res?.elements
          ?.find((el) => el.price_components?.some((p) => p?.type === "TIME"))
          ?.price_components?.find((p) => p?.type === "TIME") || null;

      const blockingFeeNum = timePriceObj
        ? (Number(timePriceObj?.price) *
            (timePriceObj?.step_size || 1) *
            vatMultiplier) /
          60
        : null;

      const timePriceRestrictions =
        res?.elements?.find((el) => el?.price_components[0]?.type === "TIME")
          ?.restrictions || null;

      const minDuration = timePriceRestrictions?.min_duration || null;
      const maxDuration = timePriceRestrictions?.max_duration || null;

      const blockingCapMinDuration =
        res?.elements?.find(
          (el) =>
            el?.price_components[0]?.type === "FLAT" &&
            el?.restrictions?.min_duration
        )?.restrictions?.min_duration || null;

      const blockingFeeCapObj =
        res?.elements?.find(
          (el) =>
            el?.price_components[0]?.type === "FLAT" &&
            el?.restrictions?.min_duration
        )?.price_components[0] || null;

      console.log("Blocking Fee Cap Raw Price:", blockingFeeCapObj?.price);
      console.log("Blocking Cap Min Duration:", blockingCapMinDuration);

      if (!blockingFeeCapObj) {
        console.warn("⚠️ No blocking fee cap found in response.");
      }

      const blockingFeeCap = blockingFeeCapObj
        ? Number(blockingFeeCapObj?.price) * vatMultiplier
        : null;

      const combinedStationPrices = {
        transactionFee,
        energyPrice,
        blockingFee: {
          price: blockingFeeNum,
          minDuration,
          maxDuration,
        },
        blockingFeeCap: {
          price: blockingFeeCap,
          minDuration: blockingCapMinDuration,
        },
      };

      console.log("✅ Computed Station Prices:", combinedStationPrices);

      setStationPrice(combinedStationPrices);
    } catch (e) {
      console.error("❌ Error Fetching Station Price:", e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    stationPrices,
    getStationPriceByType,
    lastUpdated,
    isLoading,
    originalResponse,
  };
}
